import { Component, inject, signal } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { MatDialog } from '@angular/material/dialog';
import { AuthService } from './services/auth-service';
import { environment } from '../environments/environment';
import { AuthenticationResult, InteractionStatus } from '@azure/msal-browser';
import { SpinnerService } from './services/spinner-service';
import { filter, Subject, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ProfileService } from './services/profile-service';
import { IProfileDetail } from './interfaces/profile-interface';
import { SharedServices } from './services/shared-service.service';
import { ConfirmationDialog } from './components/shared-components/confirmation-dialog/confirmation-dialog';
import { Spinner } from './components/shared-components/spinner/spinner';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, Spinner],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent {
  readonly spinnerService = inject(SpinnerService);
  protected readonly title = signal('SWP');
  isTokenExists = false;
  private isBlockingDialogOpen = false;
  private readonly _destroying$ = new Subject<void>();
  constructor(
    private msalBroadcastService: MsalBroadcastService,
    private msalService: MsalService, //private route: Router
    private dialog: MatDialog,
    private authService: AuthService,
    private translateService: TranslateService,
    private profileService: ProfileService,
    private sharedService: SharedServices,
    private router: Router,
  ) {}

  ngOnInit() {
    void this.initializeService();
  }

  async logIn() {
    await this.msalService.instance.loginRedirect({
      scopes: ['user.read', 'openid', 'profile'],
      redirectUri: environment.redirectUri,
    });
  }

  get authenticated(): boolean {
    return this.msalService.instance.getActiveAccount() ? true : false;
  }
  private async initializeService(): Promise<void> {
    try {
      await this.msalService.instance.initialize();
      const result: any =
        await this.msalService.instance.handleRedirectPromise();
      this.authService.removeAccessToken();
      if (result && result?.account) {
        // result.account.username = result?.account?.idTokenClaims['email_address'];
        this.loadMyProfileData(result);
      }
      if (!result) {
        await this.logIn();
      }

      this.msalBroadcastService.inProgress$
        .pipe(
          filter(
            (status: InteractionStatus) => status === InteractionStatus.None,
          ),
          takeUntil(this._destroying$),
        )
        .subscribe(() => {
          this.translateService.use('en-US');

          this.isTokenExists = true;
        });
    } catch (error) {
      // MSAL redirect interruptions during the auth handshake are expected; log
      // for diagnostics instead of surfacing them as an unexpected app error.
      console.error('MSAL initialization error:', error);
    }
  }
  loadMyProfileData(result: AuthenticationResult) {
    this.spinnerService.show();
    this.profileService.getProfileDetails(result?.account?.username).subscribe({
      next: (profile: IProfileDetail) => {
        this.spinnerService.hide();

        this.translateService.use(profile.languageCode || 'en-US');
        this.authService.setProfileDetail(profile);
        this.authService.setAccessRoleMatrix(profile.accessRoleMatrix ?? null);
        this.authService.setAccessToken(result?.accessToken);
        this.msalService.instance.setActiveAccount(result?.account);

        if (profile?.isUserActive === false) {
          this.showBlockingDialog('auth.inactiveUserDialog');
        } else if (profile?.isSWPUser === false) {
          this.showBlockingDialog('auth.unauthorizedDialog');
        } else {
          this.checkIfProfileIsComplete();
        }
      },
      error: (error) => {
        this.spinnerService.hide();
        this.authService.setAccessToken(result?.accessToken);
        this.msalService.instance.setActiveAccount(result?.account);
        console.error('Error loading My Profile data:', error);
      },
    });
  }
  getProfileDetail(userMail: string) {
    this.spinnerService.show();
    this.profileService.getProfileDetails(userMail).subscribe({
      next: (profile: IProfileDetail) => {
        this.spinnerService.hide();

        this.sharedService.setCwsId(profile?.cwsId ?? '');
        this.translateService.use(profile.languageCode || 'en-US');
        this.authService.setProfileDetail(profile);
        this.authService.setAccessRoleMatrix(profile.accessRoleMatrix ?? null);
        if (profile?.isUserActive === false) {
          this.showBlockingDialog('auth.inactiveUserDialog');
        } else if (profile?.isSWPUser === false) {
          this.showBlockingDialog('auth.unauthorizedDialog');
        } else {
          this.checkIfProfileIsComplete();
        }
      },
      error: (error) => {
        this.spinnerService.hide();
        console.error('Error loading My Profile data:', error);
      },
    });
  }
  checkIfProfileIsComplete() {
    if (!this.authService.getRoleConfig()?.isProfileComplete) {
      this.router.navigate(['/myProfile']);
    }
  }

  private showBlockingDialog(translationPrefix: string): void {
    if (this.isBlockingDialogOpen) {
      return;
    }

    this.isBlockingDialogOpen = true;
    this.router.navigate(['/myProfile']).finally(() => {
      const dialogRef = this.dialog.open(ConfirmationDialog, {
        width: '460px',
        disableClose: false,
        data: {
          title: this.translateService.instant(`${translationPrefix}.title`),
          message: this.translateService.instant(
            `${translationPrefix}.message`,
          ),
          confirmText: this.translateService.instant(`${translationPrefix}.ok`),
          hideCancelButton: true,
        },
      });

      dialogRef.afterClosed().subscribe(() => {
        this.isBlockingDialogOpen = false;
        this.msalService.logoutRedirect();
      });
    });
  }

  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }
}
